/**
 * PlayjectorService - Production implementation using Electron IPC
 *
 * Wraps the actual Playjector IPC communication for production use.
 * Only available when running in Electron (not in browser).
 *
 * Scope: PlayjectorCoordinator only
 */

import type {
  IPlayjectorService,
  PlayjectorState,
  CampaignLaunchResult,
  MacroStepCaptureSpec,
  MacroStepCaptureResult,
  MacroStepReply,
  MacroStepTarget,
  ScheduledScreenshotSpec,
  ScheduledScreenshotResult,
  ShadowDisplayAddDisplayResult,
  InstanceCapabilities,
  InstanceLaunchedEvent,
  InstanceClosedEvent,
  RecordingStopResult,
  SessionScreenshotResult,
} from './interfaces/IPlayjectorService';
import { ipcSend } from '../../api/messaging/protocols/ipc';
import {
  generators,
  type ProcessLaunchPackageAndroid,
} from '@playcastdotio/Messaging';
import { authState, playjectorHostModeState } from '../../api/stores/shared';
import { PlaycastHostEdition } from '@playcastdotio/CoreTypes';
import * as Sentry from '@sentry/react';
import { ShadowDisplayReplyWaiter } from './ShadowDisplayReplyWaiter';
import { buildProcessMacroStepMessage } from './buildProcessMacroStepMessage';
import { AndroidInstanceMessenger } from './AndroidInstanceMessenger';
import {
  MacroStepWaiters,
  MacroStepMonitorNotReadyError,
  MONITOR_NOT_READY_RETRY_DELAYS_MS,
} from './MacroStepWaiters';

/**
 * Timeout for recording control IPC round-trips. Recording start/stop
 * involves encoder initialization; allow generous time. Must exceed the C++
 * side's own worst case (StopBackendAndWaitForOutput polls up to 15s for MP4
 * finalization) plus transit, so a legitimate max-length stop doesn't race.
 */
const RECORDING_CONTROL_TIMEOUT_MS = 20_000;

/**
 * Timeout for the recording-state status-query round-trip. Native answers
 * synchronously from RecordControlManager getters; short headroom suffices.
 */
const RECORD_STATUS_QUERY_TIMEOUT_MS = 5_000;

/**
 * Timeout for session screenshot capture IPC round-trips. Must exceed the C++
 * side's own 10s capture wait (DisplayManager::RequestScreenshot) plus transit,
 * so a slow-but-successful capture isn't cut off by the host.
 */
const SESSION_SCREENSHOT_TIMEOUT_MS = 15_000;

/**
 * How long to wait for the encoder's `encoderStatusReplyMSG` "STARTED" signal
 * after a recording start has been acknowledged. The signal is emitted from the
 * native recording thread once frames are actually flowing; typical latency is
 * well under a second, so 10s is generous.
 */
const ENCODER_READY_TIMEOUT_MS = 10_000;

/**
 * Variant key used when requestScheduledScreenshot wraps a macro step request.
 * The watcher only needs a single capture, so we always use this key.
 */
const SCHEDULED_VARIANT_KEY = 'scheduled';

interface RecordingControlWaiter {
  resolve(value: { status: string; filePath?: string }): void;
  reject(reason: Error): void;
  timeoutId: ReturnType<typeof setTimeout>;
  takeId: string;
  sentAt: number;
}

interface SessionScreenshotWaiter {
  resolve(value: SessionScreenshotResult): void;
  reject(reason: Error): void;
  timeoutId: ReturnType<typeof setTimeout>;
  sessionName: string;
  sentAt: number;
}

interface EncoderReadyWaiter {
  resolve(): void;
  reject(reason: Error): void;
  timeoutId: ReturnType<typeof setTimeout>;
  takeId: string;
  startedWaitingAt: number;
}

interface RecordStatusQueryWaiter {
  resolve(value: { isRecording: boolean; activeTakeId: string }): void;
  reject(reason: Error): void;
  timeoutId: ReturnType<typeof setTimeout>;
}

export class PlayjectorService implements IPlayjectorService {
  private campaignLaunchListeners: Array<(result: CampaignLaunchResult) => void> = [];
  // Macro-step request/reply correlation lives in MacroStepWaiters
  // (production-readiness Phase 5, R13 — keyed by instance identity, 'legacy'
  // for the aggregate path); this service keeps the public seam and the retry
  // policy.
  private readonly macroStepWaiters = new MacroStepWaiters();
  // Android multi-instance (Phase 3): the android messaging surface (launch,
  // restart correlation, notification fan-out, stream-id registration) lives
  // in AndroidInstanceMessenger (Phase 8, review fix A5); this service keeps
  // the single mainMessage tap and delegates.
  private readonly androidMessenger = new AndroidInstanceMessenger(() => this.isAvailable());
  private pendingRecordingControl: RecordingControlWaiter | null = null;
  private pendingSessionScreenshot: SessionScreenshotWaiter | null = null;
  private pendingEncoderReady: EncoderReadyWaiter | null = null;
  private pendingRecordStatusQuery: RecordStatusQueryWaiter | null = null;

  constructor(
    private readonly shadowDisplayReplyWaiter = new ShadowDisplayReplyWaiter(),
  ) {
    if (typeof window !== 'undefined') {
      window.playcast?.ipc?.tap('mainMessage', (message) => {
        // Malformed envelope (null / missing header): host-electron only
        // forwards well-formed messages, but a corrupted sender must not
        // break the whole delivery round — log and drop.
        if (!message?.header) {
          console.warn('[PlayjectorService] Ignoring malformed mainMessage envelope (missing header)');
          return;
        }
        if (
          message.header.target === 'display'
          && message.header.action === 'addDisplay'
          && message.header.isReply
        ) {
          this.notifyShadowDisplayAddDisplayResult(
            message.body.message as ShadowDisplayAddDisplayResult,
          );
        }
        // Android multi-instance (Phases 2/3): capabilities replies,
        // per-instance launch/closed notifications, and restart replies all
        // route through the messenger (its own streamId registration gates
        // forwarding — separate from the legacy pavilionLaunchReply
        // correlation, which stays untouched).
        this.androidMessenger.handleMainMessage(message);
      });
    }
    console.log('[PlayjectorService] 🚀 Playjector service initialized (Electron mode)');
  }

  public async createVirtualDisplayForSession(
    hostAuthId: string,
    session: {
      sessionId: string;
      virtualDisplayName: string;
      virtualDisplaySerial?: string;
      displayWidth?: number;
      displayHeight?: number;
      refreshRate?: number;
    },
  ): Promise<ShadowDisplayAddDisplayResult> {
    if (!this.isAvailable()) {
      return {
        success: false,
        sessionId: session.sessionId,
        error: 'Playjector not available (not in Electron)',
      };
    }
    const pending = this.shadowDisplayReplyWaiter.wait(session.sessionId);
    ipcSend(generators.display.addDisplay(
      hostAuthId,
      session.displayWidth ?? 856,
      session.displayHeight ?? 480,
      session.refreshRate ?? 60,
      session.virtualDisplayName,
      session.virtualDisplaySerial ?? `PLAYMU-${session.sessionId}`,
      true,
      undefined,
      undefined,
      true,
      session.sessionId,
      true,
    ));
    const reply = await pending;
    if (reply.success && !reply.gdiDeviceName) {
      return {
        ...reply,
        success: false,
        error: `ShadowDisplay reply for ${session.sessionId} did not include gdiDeviceName`,
      };
    }
    return reply;
  }

  public notifyShadowDisplayAddDisplayResult(
    reply: ShadowDisplayAddDisplayResult,
  ): boolean {
    return this.shadowDisplayReplyWaiter.resolve(reply);
  }

  public removeVirtualDisplay(hostAuthId: string, guid: string): void {
    ipcSend(generators.display.removeDisplay(hostAuthId, guid));
  }

  /**
   * Fully unplug a ShadowCam on session teardown (pool-match-lifecycle
   * Phase 6): `camera.setProperties{isActive:false}` drives CameraIO's
   * EvaluatePlugState to Unplugged — device removed, pool slot released,
   * `driver_status` false. The toggle message stays pause/resume for
   * user-facing pause and is deliberately NOT used here (a pause leaves the
   * device plugged and leaks ghost cams across launch/cleanup cycles).
   */
  public unplugShadowCam(cameraName: string, hostAuthId: string): void {
    ipcSend(generators.camera.setProperties(
      cameraName,
      { cameraName, resName: '', isActive: false },
      hostAuthId,
    ));
  }

  isAvailable(): boolean {
    return typeof window !== 'undefined' && window.playcast?.isHost === true;
  }

  sendHostModeChanged(
    edition: PlaycastHostEdition,
    matchmakeable: boolean,
    steamGuestLogin: boolean,
    guestAccount: boolean,
    credentials: string,
    hostAuthId: string,
    campaignId?: string,
  ): void {
    if (!this.isAvailable()) {
      console.warn('[PlayjectorService] Playjector not available (not in Electron)');
      return;
    }

    console.log('[PlayjectorService] Sending hostModeChanged via IPC');
    // Note: campaignId is not used by real IPC (Playjector gets campaign from Electron backend)

    ipcSend(
      generators.host.hostModeChanged(
        edition,
        matchmakeable,
        steamGuestLogin,
        guestAccount,
        credentials,
        hostAuthId,
      ),
    );
  }

  killPlayjector(): void {
    if (!this.isAvailable() || !window.playcast) {
      console.warn('[PlayjectorService] Playjector not available (not in Electron)');
      return;
    }

    console.log('[PlayjectorService] Killing Playjector');
    window.playcast.killPlayjector();
  }

  terminateGame(hostAuthId: string): void {
    if (!this.isAvailable()) {
      console.warn('[PlayjectorService] Playjector not available (not in Electron)');
      return;
    }

    console.log('[PlayjectorService] Terminating game process (pavilion)');
    ipcSend(generators.process.processTerminate(hostAuthId, 'pavilion'));
  }

  connectPlayjector(): void {
    if (!this.isAvailable() || !window.playcast) {
      console.warn('[PlayjectorService] Playjector not available (not in Electron)');
      return;
    }

    console.log('[PlayjectorService] Connecting to Playjector');
    window.playcast.connectPlayjector();
  }

  onStateChange(callback: (state: PlayjectorState) => void): () => void {
    // Subscribe to the shared playjectorHostModeState store
    const listenerId = playjectorHostModeState.addListener((state) => {
      callback(state as PlayjectorState);
    });

    // Return unsubscribe function
    return () => {
      playjectorHostModeState.removeListener(listenerId);
    };
  }

  getCurrentState(): PlayjectorState {
    return playjectorHostModeState.currentState as PlayjectorState;
  }

  onCampaignLaunchResult(callback: (result: CampaignLaunchResult) => void): () => void {
    // Add listener
    this.campaignLaunchListeners.push(callback);

    // TODO: Hook into IPC pavilionLaunchReply messages
    // The IPC handler (apps/host/host-frontend/src/api/messaging/handlers/ipc.ts)
    // currently sends these directly to campaignSwitchTracker.
    // In the new architecture, we should route through this service.
    console.warn('[PlayjectorService] onCampaignLaunchResult: IPC integration not yet implemented');

    // Return unsubscribe function
    return () => {
      const index = this.campaignLaunchListeners.indexOf(callback);
      if (index > -1) {
        this.campaignLaunchListeners.splice(index, 1);
      }
    };
  }

  launchCampaign(hostAuthId: string, edition: PlaycastHostEdition, launchPackage: any): void {
    if (!this.isAvailable()) {
      console.warn('[PlayjectorService] Playjector not available (not in Electron)');
      return;
    }

    console.log('[PlayjectorService] 🎮 Launching campaign via processLaunch IPC:', {
      edition,
      launcher: launchPackage.launcher,
      hostAuthId,
    });

    Sentry.logger.info(`[PlayjectorService] 🎮 Launching campaign via processLaunch IPC: ${edition}, ${launchPackage.launcher}`);

    ipcSend(
      generators.process.processLaunch(
        hostAuthId,
        edition,
        launchPackage,
      ),
    );
  }

  /**
   * Internal: Notify campaign launch listeners
   * This will be called by IPC handler when pavilionLaunchReply is received
   */
  public notifyCampaignLaunchResult(result: CampaignLaunchResult): void {
    console.log('[PlayjectorService] 📨 Notifying listeners of campaign launch result:', result);
    this.campaignLaunchListeners.forEach((listener) => {
      try {
        listener(result);
      } catch (error) {
        console.error('[PlayjectorService] Campaign launch listener error:', error);
      }
    });
  }

  // ========================================
  // Stateful macro step IPC adapter
  // ========================================

  public async requestMacroStep(
    captures: Record<string, MacroStepCaptureSpec>,
    screenshotName?: string,
    target?: MacroStepTarget,
  ): Promise<Record<string, MacroStepCaptureResult>> {
    if (!this.isAvailable()) {
      throw new Error('Playjector not available (not in Electron)');
    }

    if (Object.keys(captures).length === 0) {
      throw new Error('requestMacroStep called with empty captures map');
    }

    const variants: Record<string, MacroStepCaptureSpec> = {};
    for (const [captureId, spec] of Object.entries(captures)) {
      variants[captureId] = spec.region
        ? { width: spec.width, height: spec.height, region: spec.region }
        : { width: spec.width, height: spec.height };
    }

    // Keyed waiters (production-readiness Phase 5, R13 / macro G2): each
    // instance target gets its own in-flight slot; the untargeted aggregate
    // path keeps the historical single-flight rule via the 'legacy' key.
    const waiterKey = MacroStepWaiters.keyForTarget(target);

    // "monitor not ready" retry (macro G9): the C++ instance path returns
    // exactly that error while the target instance's DisplayGrabber is still
    // converging. Bounded: 3 retries (500ms/1s/2s) after the initial attempt,
    // then the failure surfaces to the caller.
    // Worst-case latency before surfacing (Phase 8 review nit N9): 4 sends ×
    // the 10s reply timeout + 3.5s backoff ≈ 43.5s per step — any walk-level
    // deadline must budget for it.
    for (let attempt = 0; ; attempt += 1) {
      try {
        return await this.requestMacroStepOnce(waiterKey, variants, screenshotName, target);
      } catch (error) {
        const retryDelayMs = MONITOR_NOT_READY_RETRY_DELAYS_MS[attempt];
        if (!(error instanceof MacroStepMonitorNotReadyError) || retryDelayMs === undefined) {
          throw error;
        }
        console.warn(
          '[StatefulMacroSystemStep] 🔁 Playjector replied "monitor not ready" — retrying macro step',
          { waiterKey, attempt: attempt + 1, retryDelayMs },
        );
        await new Promise<void>((resolve) => {
          setTimeout(resolve, retryDelayMs);
        });
      }
    }
  }

  /**
   * One macro-step send + wait for the keyed waiter. `begin` throws
   * synchronously when the key is already in flight (per-key single-flight),
   * which surfaces to the caller as a rejection — the pre-Phase-5 behavior
   * for the legacy key.
   */
  private async requestMacroStepOnce(
    waiterKey: string,
    variants: Record<string, MacroStepCaptureSpec>,
    screenshotName?: string,
    target?: MacroStepTarget,
  ): Promise<Record<string, MacroStepCaptureResult>> {
    const message = buildProcessMacroStepMessage(variants, screenshotName, target);
    const promise = this.macroStepWaiters.begin(waiterKey);

    console.log(
      '[StatefulMacroSystemStep] 📤 Asking Playjector for screenshot(s) (processMacroStepMSG)',
      {
        variantCount: Object.keys(variants).length,
        captureIds: Object.keys(variants),
        variants: Object.fromEntries(
          Object.entries(variants).map(([id, spec]) => [
            id,
            {
              width: spec.width,
              height: spec.height,
              hasRegion: 'region' in spec && spec.region != null,
            },
          ]),
        ),
        ...(target ? { target } : {}),
      },
    );
    ipcSend(message);

    return promise;
  }

  public notifyMacroStepResult(
    reply: MacroStepReply & { error?: string; streamId?: string; gameInstanceIndex?: number },
  ): void {
    // Demux by echoed instance identity lives in MacroStepWaiters (macro G3):
    // a reply carrying streamId + gameInstanceIndex settles that instance's
    // waiter; a reply without identity settles the legacy aggregate waiter.
    this.macroStepWaiters.notify(reply);
  }

  /**
   * Reject and clear every in-flight `requestMacroStep` waiter (walk
   * teardown / campaign abandonment). Callers don't track which per-instance
   * keys, if any, still have an outstanding request, so this abandons all of
   * them via `MacroStepWaiters`.
   */
  public abandonMacroStep(): void {
    this.macroStepWaiters.abandonAll();
  }

  // ========================================
  // Scheduled screenshot (delegates to macro step)
  // ========================================

  public async requestScheduledScreenshot(
    spec: ScheduledScreenshotSpec,
  ): Promise<ScheduledScreenshotResult> {
    const captures: Record<string, MacroStepCaptureSpec> = {
      [SCHEDULED_VARIANT_KEY]: { width: spec.width, height: spec.height },
    };

    const results = await this.requestMacroStep(captures, 'scheduled_latest');
    const result = results[SCHEDULED_VARIANT_KEY];

    if (!result) {
      throw new Error('Scheduled screenshot: no result returned for variant');
    }

    return { hash: result.hash, width: result.width, height: result.height };
  }

  public notifyScheduledScreenshotResult(
    _reply: ScheduledScreenshotResult & { error?: string },
  ): void {
    // No-op: scheduled screenshots now route through the macro step reply path.
    // Kept for interface compatibility with MockPlayjectorService.
    console.warn('[ScheduledScreenshot] notifyScheduledScreenshotResult is deprecated — replies route via macroStep');
  }

  // ========================================
  // Recording control IPC adapter
  // ========================================

  public async startRecording(takeId: string, cameraName?: string): Promise<void> {
    if (!this.isAvailable()) {
      throw new Error('Playjector not available (not in Electron)');
    }
    if (this.pendingRecordingControl) {
      throw new Error('A recording control request is already in flight');
    }

    const message = generators.recordControl.startRecording('host', takeId, cameraName);
    const sendTs = Date.now();
    const promise = new Promise<{ status: string; filePath?: string }>((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        this.pendingRecordingControl = null;
        Sentry.logger.info('[PlayjectorService] macro-recorder startRecording timed out', {
          feature: 'macro-recorder', takeId, timeoutMs: RECORDING_CONTROL_TIMEOUT_MS,
        });
        reject(new Error(`startRecording timed out after ${RECORDING_CONTROL_TIMEOUT_MS}ms`));
      }, RECORDING_CONTROL_TIMEOUT_MS);
      this.pendingRecordingControl = { resolve, reject, timeoutId, takeId, sentAt: sendTs };
    });

    Sentry.logger.info('[PlayjectorService] macro-recorder IPC send startRecording', {
      feature: 'macro-recorder', transport: 'ipc', direction: 'send',
      action: 'startRecording', takeId, cameraName: cameraName ?? 'default',
    });
    console.log('[PlayjectorService] 📤 Sending startRecording IPC', { takeId });
    ipcSend(message);

    const result = await promise;
    if (result.status !== 'started') {
      throw new Error(`startRecording unexpected status: ${result.status}`);
    }
  }

  public async stopRecording(takeId: string): Promise<RecordingStopResult> {
    if (!this.isAvailable()) {
      throw new Error('Playjector not available (not in Electron)');
    }
    if (this.pendingRecordingControl) {
      throw new Error('A recording control request is already in flight');
    }

    const message = generators.recordControl.stopRecording('host', takeId);
    const sendTs = Date.now();
    const promise = new Promise<{ status: string; filePath?: string }>((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        this.pendingRecordingControl = null;
        Sentry.logger.info('[PlayjectorService] macro-recorder stopRecording timed out', {
          feature: 'macro-recorder', takeId, timeoutMs: RECORDING_CONTROL_TIMEOUT_MS,
        });
        reject(new Error(`stopRecording timed out after ${RECORDING_CONTROL_TIMEOUT_MS}ms`));
      }, RECORDING_CONTROL_TIMEOUT_MS);
      this.pendingRecordingControl = { resolve, reject, timeoutId, takeId, sentAt: sendTs };
    });

    Sentry.logger.info('[PlayjectorService] macro-recorder IPC send stopRecording', {
      feature: 'macro-recorder', transport: 'ipc', direction: 'send',
      action: 'stopRecording', takeId,
    });
    console.log('[PlayjectorService] 📤 Sending stopRecording IPC', { takeId });
    ipcSend(message);

    const result = await promise;
    if (result.status !== 'finished') {
      throw new Error(`stopRecording unexpected status: ${result.status}`);
    }
    if (!result.filePath) {
      throw new Error('stopRecording reply missing filePath');
    }
    return { filePath: result.filePath };
  }

  public notifyRecordControlStatus(reply: {
    status: string;
    takeId: string;
    filePath?: string;
    error?: string;
  }): void {
    const waiter = this.pendingRecordingControl;
    if (!waiter) {
      Sentry.logger.info('[PlayjectorService] macro-recorder recordControlStatus reply with no waiter', {
        feature: 'macro-recorder', takeId: reply.takeId, status: reply.status,
      });
      console.warn('[PlayjectorService] recordControlStatus reply with no waiter — dropping');
      return;
    }
    if (waiter.takeId !== reply.takeId) {
      Sentry.logger.info('[PlayjectorService] macro-recorder recordControlStatus takeId mismatch', {
        feature: 'macro-recorder', expected: waiter.takeId, received: reply.takeId,
      });
      console.warn('[PlayjectorService] recordControlStatus takeId mismatch — dropping', {
        expected: waiter.takeId,
        received: reply.takeId,
      });
      return;
    }
    this.pendingRecordingControl = null;
    clearTimeout(waiter.timeoutId);

    const latencyMs = Date.now() - waiter.sentAt;
    Sentry.logger.info('[PlayjectorService] macro-recorder notifyRecordControlStatus resolved', {
      feature: 'macro-recorder', takeId: reply.takeId, status: reply.status, latencyMs,
    });

    if (reply.error) {
      waiter.reject(new Error(`Recording control error: ${reply.error}`));
      return;
    }
    waiter.resolve({ status: reply.status, filePath: reply.filePath });
  }

  public async queryRecordingState(): Promise<{ isRecording: boolean; activeTakeId: string }> {
    if (!this.isAvailable()) {
      throw new Error('Playjector not available (not in Electron)');
    }
    if (this.pendingRecordStatusQuery) {
      throw new Error('A recording state query is already in flight');
    }

    const message = generators.recordControl.statusQuery('host');

    const promise = new Promise<{ isRecording: boolean; activeTakeId: string }>((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        this.pendingRecordStatusQuery = null;
        Sentry.logger.info('[PlayjectorService] macro-recorder statusQuery timed out', {
          feature: 'macro-recorder', timeoutMs: RECORD_STATUS_QUERY_TIMEOUT_MS,
        });
        reject(new Error(`statusQuery timed out after ${RECORD_STATUS_QUERY_TIMEOUT_MS}ms`));
      }, RECORD_STATUS_QUERY_TIMEOUT_MS);
      this.pendingRecordStatusQuery = { resolve, reject, timeoutId };
    });

    Sentry.logger.info('[PlayjectorService] macro-recorder IPC send statusQuery', {
      feature: 'macro-recorder', transport: 'ipc', direction: 'send',
      action: 'statusQuery',
    });
    ipcSend(message);

    return promise;
  }

  public notifyRecordStatusQuery(reply: { isRecording?: boolean; activeTakeId?: string }): void {
    const waiter = this.pendingRecordStatusQuery;
    if (!waiter) {
      console.warn('[PlayjectorService] recordStatusQuery reply with no waiter — dropping');
      Sentry.logger.info('[PlayjectorService] macro-recorder recordStatusQuery dropped — no waiter', {
        feature: 'macro-recorder',
      });
      return;
    }
    this.pendingRecordStatusQuery = null;
    clearTimeout(waiter.timeoutId);

    const isRecording = reply.isRecording === true;
    const activeTakeId = typeof reply.activeTakeId === 'string' ? reply.activeTakeId : '';
    Sentry.logger.info('[PlayjectorService] macro-recorder recordStatusQuery resolved', {
      feature: 'macro-recorder', isRecording, activeTakeId,
    });
    waiter.resolve({ isRecording, activeTakeId });
  }

  // ========================================
  // Encoder readiness (encoderStatusReplyMSG)
  // ========================================

  public waitForEncoderReady(takeId: string): Promise<void> {
    if (!this.isAvailable()) {
      return Promise.reject(new Error('Playjector not available (not in Electron)'));
    }
    if (this.pendingEncoderReady) {
      return Promise.reject(new Error('An encoder-ready wait is already in flight'));
    }

    return new Promise<void>((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        this.pendingEncoderReady = null;
        Sentry.logger.info('[PlayjectorService] macro-recorder waitForEncoderReady timed out', {
          feature: 'macro-recorder', takeId, timeoutMs: ENCODER_READY_TIMEOUT_MS,
        });
        reject(new Error(`waitForEncoderReady timed out after ${ENCODER_READY_TIMEOUT_MS}ms`));
      }, ENCODER_READY_TIMEOUT_MS);
      this.pendingEncoderReady = { resolve, reject, timeoutId, takeId, startedWaitingAt: Date.now() };
    });
  }

  public notifyEncoderStatus(reply: { cameraName?: string; result: string }): void {
    const waiter = this.pendingEncoderReady;
    if (!waiter) {
      // No waiter: the legacy game-clip path also emits encoder status updates,
      // so an unsolicited status is normal — stay silent.
      return;
    }
    // Native sends uppercase ("STARTED"/"FINISHED"); normalize defensively.
    if (reply.result?.toLowerCase() !== 'started') {
      return;
    }
    this.pendingEncoderReady = null;
    clearTimeout(waiter.timeoutId);

    const latencyMs = Date.now() - waiter.startedWaitingAt;
    Sentry.logger.info('[PlayjectorService] macro-recorder encoder STARTED — waiter resolved', {
      feature: 'macro-recorder', takeId: waiter.takeId, cameraName: reply.cameraName, latencyMs,
    });
    waiter.resolve();
  }

  // ========================================
  // Session screenshot IPC adapter
  // ========================================

  public async captureSessionScreenshot(
    sessionName: string,
    width: number,
    height: number,
  ): Promise<SessionScreenshotResult> {
    if (!this.isAvailable()) {
      Sentry.captureMessage('[MACRO-SCREENSHOT] captureSessionScreenshot rejected — Playjector not available', {
        level: 'error',
        extra: { sessionName },
      });
      throw new Error('Playjector not available (not in Electron)');
    }
    if (this.pendingSessionScreenshot) {
      // The single in-flight slot is occupied. Capture what is holding it vs the
      // request we just rejected — this is the top signal for "in flight forever".
      Sentry.captureMessage('[MACRO-SCREENSHOT] captureSessionScreenshot rejected — already in flight', {
        level: 'warning',
        extra: {
          rejectedSessionName: sessionName,
          pendingSessionName: this.pendingSessionScreenshot.sessionName,
        },
      });
      throw new Error('A session screenshot request is already in flight');
    }

    const message = generators.screenshotSession.captureScreenshot('host', sessionName, width, height);

    const sendTs = Date.now();
    const promise = new Promise<SessionScreenshotResult>((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        this.pendingSessionScreenshot = null;
        Sentry.logger.info('[PlayjectorService] macro-recorder captureSessionScreenshot timed out', {
          feature: 'macro-recorder', sessionName, timeoutMs: SESSION_SCREENSHOT_TIMEOUT_MS,
        });
        Sentry.captureMessage('[MACRO-SCREENSHOT] captureSessionScreenshot timed out — no Playjector reply', {
          level: 'error',
          extra: { sessionName, timeoutMs: SESSION_SCREENSHOT_TIMEOUT_MS },
        });
        reject(new Error(`captureSessionScreenshot timed out after ${SESSION_SCREENSHOT_TIMEOUT_MS}ms`));
      }, SESSION_SCREENSHOT_TIMEOUT_MS);
      this.pendingSessionScreenshot = { resolve, reject, timeoutId, sessionName, sentAt: sendTs };
    });

    Sentry.logger.info('[PlayjectorService] macro-recorder IPC send captureSessionScreenshot', {
      feature: 'macro-recorder', transport: 'ipc', direction: 'send',
      action: 'captureSessionScreenshot', sessionName, width, height,
    });
    console.log('[PlayjectorService] 📤 Sending captureSessionScreenshot IPC', { sessionName, width, height });
    Sentry.addBreadcrumb({
      category: 'MACRO-SCREENSHOT',
      level: 'info',
      message: 'sending captureSessionScreenshot IPC',
      data: { sessionName, width, height },
    });
    ipcSend(message);

    return promise;
  }

  public notifySessionScreenshotResult(reply: {
    result: string;
    sessionName: string;
    index: number;
    filePath: string;
    timestampMs: number;
    error?: string;
  }): void {
    const waiter = this.pendingSessionScreenshot;
    if (!waiter) {
      console.warn('[PlayjectorService] sessionScreenshot reply with no waiter — dropping');
      Sentry.captureMessage('[MACRO-SCREENSHOT] sessionScreenshot reply dropped — no waiter', {
        level: 'warning',
        extra: { sessionName: reply.sessionName, result: reply.result, error: reply.error },
      });
      return;
    }
    if (waiter.sessionName !== reply.sessionName) {
      console.warn('[PlayjectorService] sessionScreenshot sessionName mismatch — dropping');
      Sentry.captureMessage('[MACRO-SCREENSHOT] sessionScreenshot reply dropped — sessionName mismatch', {
        level: 'warning',
        extra: { expected: waiter.sessionName, received: reply.sessionName },
      });
      return;
    }
    this.pendingSessionScreenshot = null;
    clearTimeout(waiter.timeoutId);

    const latencyMs = Date.now() - waiter.sentAt;

    if (reply.error) {
      Sentry.logger.info('[PlayjectorService] macro-recorder notifySessionScreenshotResult error', {
        feature: 'macro-recorder', sessionName: reply.sessionName, error: reply.error, latencyMs,
      });
      Sentry.addBreadcrumb({
        category: 'MACRO-SCREENSHOT',
        level: 'warning',
        message: 'sessionScreenshot error reply',
        data: { sessionName: reply.sessionName, error: reply.error },
      });
      waiter.reject(new Error(`Session screenshot error: ${reply.error}`));
      return;
    }
    Sentry.logger.info('[PlayjectorService] macro-recorder notifySessionScreenshotResult resolved', {
      feature: 'macro-recorder', sessionName: reply.sessionName, index: reply.index, latencyMs,
    });
    Sentry.addBreadcrumb({
      category: 'MACRO-SCREENSHOT',
      level: 'info',
      message: 'sessionScreenshot resolved',
      data: {
        sessionName: reply.sessionName,
        index: reply.index,
        filePath: reply.filePath,
        timestampMs: reply.timestampMs,
      },
    });
    waiter.resolve({ index: reply.index, filePath: reply.filePath, timestampMs: reply.timestampMs });
  }

  // ========================================
  // Android multi-instance (implemented Phase 2: capabilities; Phase 3: launch)
  //
  // The launch/restart/notification messaging lives in AndroidInstanceMessenger
  // (Phase 8, review fix A5) — the android methods below delegate to it.
  // ========================================

  /**
   * Query the machine's multi-instance capabilities
   * (`processCapabilitiesQueryMSG` → `processCapabilitiesReplyMSG`).
   * Implementation lives in AndroidInstanceMessenger (A5).
   */
  public async getInstanceCapabilities(): Promise<InstanceCapabilities> {
    return this.androidMessenger.getInstanceCapabilities();
  }

  /**
   * Resolve the in-flight capabilities query with a
   * `processCapabilitiesReplyMSG` payload. Kept on the service for IPC-tap
   * compatibility; delegates to AndroidInstanceMessenger (A5).
   */
  public notifyInstanceCapabilitiesReply(reply: InstanceCapabilities): void {
    this.androidMessenger.notifyInstanceCapabilitiesReply(reply);
  }

  /**
   * Launch N android game instances from one launch package (`processLaunchMSG`
   * with an android `launchPackage`) and register its `streamId` for reply
   * routing. Implementation lives in AndroidInstanceMessenger (A5).
   */
  public async launchAndroidInstances(pkg: ProcessLaunchPackageAndroid): Promise<void> {
    return this.androidMessenger.launchAndroidInstances(pkg);
  }

  /**
   * Stop routing `instanceLaunched`/`instanceClosed` notifications for a
   * torn-down android stream (Phase 8, review fix S1).
   */
  public unregisterAndroidStream(streamId: string): void {
    this.androidMessenger.unregisterAndroidStream(streamId);
  }

  /**
   * Restart a single game instance (`processRestartInstanceMSG`); sibling
   * instances are untouched. Implementation lives in AndroidInstanceMessenger
   * (A5).
   */
  public async restartInstance(streamId: string, gameInstanceIndex: number): Promise<string> {
    return this.androidMessenger.restartInstance(streamId, gameInstanceIndex);
  }

  /**
   * Resolve the in-flight `restartInstance` wait for a
   * `processRestartInstanceReplyMSG` payload. Kept on the service for IPC-tap
   * compatibility; delegates to AndroidInstanceMessenger (A5).
   */
  public notifyRestartInstanceReply(reply: {
    streamId: string;
    gameInstanceIndex: number;
    result: string;
  }): void {
    this.androidMessenger.notifyRestartInstanceReply(reply);
  }

  /**
   * Subscribe to per-instance launch notifications
   * (`processInstanceLaunchedMSG`). Only notifications for streamIds
   * registered via `launchAndroidInstances` are forwarded. Returns an
   * unsubscribe function.
   */
  public onInstanceLaunched(callback: (event: InstanceLaunchedEvent) => void): () => void {
    return this.androidMessenger.onInstanceLaunched(callback);
  }

  /**
   * Subscribe to per-instance closed notifications
   * (`processInstanceClosedMSG`). The match lifecycle consumes these via
   * `coordinators/init.ts` (Phase 4). Returns an unsubscribe function.
   */
  public onInstanceClosed(callback: (event: InstanceClosedEvent) => void): () => void {
    return this.androidMessenger.onInstanceClosed(callback);
  }
}
